/**
 * The subtitle strip's maths (1.9.0), pure so it is unit tested: the window that follows the playhead, pixels and
 * ticks, what a press hits, the cues after a drag (contracts setCueEdge / moveCue: frame snapping, neighbour clamps),
 * and the ruler's steps.
 */
import { DEFAULT_SUBTITLE_RULES, TICKS_PER_SECOND, cueMetrics, moveCue, setCueEdge, type Cue, type CueEditContext, type TrackStyle } from '@prism/contracts';

export type StripPart = 'start' | 'end' | 'body';

/** The edge zone of a selected cue, in px. */
export const EDGE_PX = 6;

/**
 * Where the window starts, following the playhead: kept while the playhead is inside its first 90 %, otherwise moved
 * so the playhead sits a quarter in (never before 0, never past the end).
 */
export function followWindow(start: number, playhead: number, windowTicks: number, durationTicks: number): number {
  const max = Math.max(0, durationTicks - windowTicks);
  const clamp = (t: number) => Math.max(0, Math.min(max, Math.round(t)));
  if (playhead >= start && playhead <= start + windowTicks * 0.9) return clamp(start);
  return clamp(playhead - windowTicks / 4);
}

/** The window's start when its length changes: the playhead stays where it is on screen. */
export function zoomWindow(start: number, playhead: number, fromTicks: number, toTicks: number, durationTicks: number): number {
  const share = fromTicks > 0 ? Math.min(1, Math.max(0, (playhead - start) / fromTicks)) : 0.25;
  return Math.max(0, Math.min(Math.max(0, durationTicks - toTicks), Math.round(playhead - share * toTicks)));
}

export const ticksToX = (ticks: number, start: number, windowTicks: number, width: number): number => ((ticks - start) / windowTicks) * width;
export const xToTicks = (x: number, start: number, windowTicks: number, width: number): number => start + (x / Math.max(1, width)) * windowTicks;

/** What a press at `x` (px in the strip) hits on a cue drawn from x0 to x1: an edge of a selected cue, else the body. */
export function hitPart(x: number, x0: number, x1: number, selected: boolean): StripPart {
  if (!selected || x1 - x0 < EDGE_PX * 3) return 'body';
  if (x - x0 <= EDGE_PX) return 'start';
  if (x1 - x <= EDGE_PX) return 'end';
  return 'body';
}

/** The cues after dragging a cue's part by `dx` px (the same array when nothing moves). */
export function dragCues(cues: Cue[], id: string, part: StripPart, dx: number, windowTicks: number, width: number, ctx: CueEditContext): Cue[] {
  const c = cues.find((x) => x.id === id);
  if (!c || !dx) return cues;
  const dt = (dx / Math.max(1, width)) * windowTicks;
  if (part === 'body') return moveCue(cues, id, c.startTicks + dt, ctx);
  return setCueEdge(cues, id, part, (part === 'start' ? c.startTicks : c.endTicks) + dt, ctx);
}

const STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600];

/** The ruler's step in seconds: the smallest that keeps labels `minPx` apart. */
export function rulerStep(windowSeconds: number, width: number, minPx = 96): number {
  const perSecond = width / Math.max(1e-9, windowSeconds);
  return STEPS.find((s) => s * perSecond >= minPx) ?? STEPS[STEPS.length - 1]!;
}

/** The ruler's marks (ticks) inside the window. */
export function rulerMarks(start: number, windowTicks: number, stepSeconds: number): number[] {
  const step = stepSeconds * TICKS_PER_SECOND;
  const out: number[] = [];
  for (let t = Math.ceil(start / step) * step; t <= start + windowTicks; t += step) out.push(t);
  return out;
}

/** The next zoom: halves or doubles the window, within the limits. */
export function nextZoom(seconds: number, dir: 1 | -1, min: number, max: number): number {
  const ladder = [5, 10, 20, 40, 60, 120, 180, 300];
  const all = [...new Set([min, ...ladder, max])].filter((s) => s >= min && s <= max).sort((a, b) => a - b);
  if (dir === 1) return [...all].reverse().find((s) => s < seconds - 1e-9) ?? min;
  return all.find((s) => s > seconds + 1e-9) ?? max;
}

export type CueSeverity = 'error' | 'warning';

/**
 * The worst issue of each cue (cueMetrics with the default rules, same-row overlaps both ways), for the strip's and the
 * timeline ticks' colours: a cue without issues is not in the map.
 */
export function cueSeverities(cues: readonly Cue[], trackStyle: TrackStyle | null | undefined, durationTicks?: number): Map<string, CueSeverity> {
  const out = new Map<string, CueSeverity>();
  for (const c of cues) {
    const { issues } = cueMetrics(c, DEFAULT_SUBTITLE_RULES, { cues, trackStyle, ...(durationTicks && durationTicks > 0 ? { durationTicks } : {}) });
    if (issues.some((i) => i.severity === 'error')) out.set(c.id, 'error');
    else if (issues.length) out.set(c.id, 'warning');
  }
  return out;
}
